import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../config/api';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

interface DailyComparisonViewProps {
  initialDate?: string;
  coachMode?: boolean;
  clientId?: number;
}

export const DailyComparisonView: React.FC<DailyComparisonViewProps> = ({
  initialDate,
  coachMode,
  clientId,
}) => {
  const [selectedDate, setSelectedDate] = useState(
    initialDate || new Date().toISOString().split('T')[0]
  );

  // Fetch comparison data
  const { data: comparisonData, isLoading, error } = useQuery({
    queryKey: coachMode
      ? ['daily-comparison', selectedDate, 'client', clientId]
      : ['daily-comparison', selectedDate],
    queryFn: () => apiClient.getDailyComparison(selectedDate, coachMode ? clientId : undefined),
    enabled: true,
  });

  const navigateDate = (direction: 'prev' | 'next') => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + (direction === 'next' ? 1 : -1));
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToCheck = new Date(dateString);
    dateToCheck.setHours(0, 0, 0, 0);

    const diffDays = Math.round((dateToCheck.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === -1) return 'Yesterday';
    if (diffDays === 1) return 'Tomorrow';

    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const formatMealType = (type: string) => {
    const labels: Record<string, string> = {
      breakfast: 'Breakfast',
      morning_snack: 'Morning Snack',
      lunch: 'Lunch',
      afternoon_snack: 'Afternoon Snack',
      dinner: 'Dinner',
      evening_snack: 'Evening Snack',
      snack: 'Snack',
      other: 'Other',
    };
    return labels[type] || type;
  };

  const getMealTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      breakfast: '🌅',
      morning_snack: '🍎',
      lunch: '🥗',
      afternoon_snack: '🥜',
      dinner: '🍽️',
      evening_snack: '🌙',
      snack: '🍪',
      other: '📦',
    };
    return icons[type] || '🍴';
  };

  const getVarianceColor = (variance: number, nutrient: string) => {
    const threshold = nutrient === 'calories' ? 100 : 10;
    if (Math.abs(variance) <= threshold) return 'text-green-600';
    if (variance > 0) return 'text-orange-600';
    return 'text-red-600';
  };

  const getVarianceIcon = (variance: number) => {
    if (variance > 5) return <ArrowTrendingUpIcon className="h-4 w-4 text-orange-500" />;
    if (variance < -5) return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
    return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
  };

  const getAdherenceColor = (percentage: number | null) => {
    if (percentage === null) return 'bg-gray-100 text-gray-600';
    if (percentage >= 90) return 'bg-green-100 text-green-800';
    if (percentage >= 75) return 'bg-blue-100 text-blue-800';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3" />
          <div className="h-32 bg-gray-100 rounded" />
          <div className="space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-100 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center">
        <ExclamationCircleIcon className="h-12 w-12 mx-auto text-red-400" />
        <p className="mt-2 text-red-600">Failed to load comparison data</p>
      </div>
    );
  }

  const { activePlan, plannedMeals, actualMeals, comparison, adherence, mealTypes } =
    comparisonData || {};

  return (
    <div className="space-y-4">
      {/* Date Navigation */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateDate('prev')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>

          <div className="flex items-center gap-2">
            <CalendarDaysIcon className="h-5 w-5 text-blue-600" />
            <span className="font-semibold text-gray-900">{formatDate(selectedDate)}</span>
            <span className="text-sm text-gray-500">{selectedDate}</span>
          </div>

          <button
            onClick={() => navigateDate('next')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Active Plan Banner */}
      {activePlan ? (
        <div className="bg-gradient-to-r from-blue-500 to-blue-700 rounded-lg p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold">{activePlan.name}</h3>
              <p className="text-sm text-blue-100">
                {new Date(activePlan.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {new Date(activePlan.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </p>
            </div>
            {adherence && (
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getAdherenceColor(adherence.percentage)}`}>
                {adherence.percentage !== null ? `${adherence.percentage}%` : 'N/A'}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-gray-100 rounded-lg p-4 text-center">
          <p className="text-gray-600">No active meal plan for this date</p>
        </div>
      )}

      {/* Totals Comparison */}
      {comparison && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <h3 className="font-semibold text-gray-900 mb-4">Daily Totals</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {['calories', 'protein', 'carbs', 'fat', 'fiber'].map((nutrient) => {
              const planned = comparison.planned[nutrient as keyof typeof comparison.planned] || 0;
              const actual = comparison.actual[nutrient as keyof typeof comparison.actual] || 0;
              const variance = comparison.variance[nutrient as keyof typeof comparison.variance] || 0;
              const unit = nutrient === 'calories' ? '' : 'g';

              return (
                <div key={nutrient} className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 uppercase mb-1">{nutrient}</div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Planned:</span>
                      <span className="font-medium">{Math.round(planned)}{unit}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Actual:</span>
                      <span className="font-medium">{Math.round(actual)}{unit}</span>
                    </div>
                    <div className={`flex items-center justify-between text-sm ${getVarianceColor(variance, nutrient)}`}>
                      <span>Diff:</span>
                      <div className="flex items-center gap-1">
                        {getVarianceIcon(variance)}
                        <span className="font-medium">
                          {variance > 0 ? '+' : ''}{Math.round(variance)}{unit}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Adherence Message */}
      {adherence && (
        <div className={`rounded-lg p-4 ${getAdherenceColor(adherence.percentage)}`}>
          <p className="text-sm font-medium">{adherence.message}</p>
        </div>
      )}

      {/* Meal-by-Meal Comparison */}
      <div className="space-y-4">
        <h3 className="font-semibold text-gray-900">Meal Breakdown</h3>
        {(mealTypes || ['breakfast', 'morning_snack', 'lunch', 'afternoon_snack', 'dinner', 'evening_snack']).map((mealType: string) => {
          const planned = plannedMeals?.[mealType] || [];
          const actual = actualMeals?.[mealType] || [];
          const hasPlanned = planned.length > 0;
          const hasActual = actual.length > 0;

          if (!hasPlanned && !hasActual) return null;

          return (
            <div key={mealType} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Meal Type Header */}
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getMealTypeIcon(mealType)}</span>
                  <span className="font-medium text-gray-900">{formatMealType(mealType)}</span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                {/* Planned Column */}
                <div className="p-4">
                  <div className="text-xs text-gray-500 uppercase mb-2 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    Planned
                  </div>
                  {hasPlanned ? (
                    <div className="space-y-2">
                      {planned.map((meal: any) => (
                        <div key={meal.id} className="bg-blue-50 rounded-lg p-3">
                          <div className="font-medium text-blue-900 text-sm">{meal.name}</div>
                          <div className="flex flex-wrap gap-2 mt-1 text-xs text-blue-700">
                            <span>{Math.round(meal.nutrition?.calories || 0)} cal</span>
                            <span>{Math.round(meal.nutrition?.protein || 0)}g P</span>
                            <span>{Math.round(meal.nutrition?.carbs || 0)}g C</span>
                            <span>{Math.round(meal.nutrition?.fat || 0)}g F</span>
                          </div>
                          {(meal.prepTime || meal.cookTime) && (
                            <div className="text-xs text-blue-600 mt-1">
                              {meal.prepTime ? `${meal.prepTime}min prep` : ''}
                              {meal.prepTime && meal.cookTime ? ' + ' : ''}
                              {meal.cookTime ? `${meal.cookTime}min cook` : ''}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 italic">No meal planned</div>
                  )}
                </div>

                {/* Actual Column */}
                <div className="p-4">
                  <div className="text-xs text-gray-500 uppercase mb-2 flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    Actual
                  </div>
                  {hasActual ? (
                    <div className="space-y-2">
                      {actual.map((meal: any) => (
                        <div key={meal.id} className="bg-green-50 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            {meal.imagePath && (
                              <img
                                src={`${meal.imagePath}`}
                                alt={meal.name}
                                className="w-12 h-12 rounded object-cover flex-shrink-0"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-green-900 text-sm truncate">
                                {meal.name}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-1 text-xs text-green-700">
                                <span>{Math.round(meal.nutrition?.calories || 0)} cal</span>
                                <span>{Math.round(meal.nutrition?.protein || 0)}g P</span>
                                <span>{Math.round(meal.nutrition?.carbs || 0)}g C</span>
                                <span>{Math.round(meal.nutrition?.fat || 0)}g F</span>
                              </div>
                              {meal.foods && meal.foods.length > 0 && (
                                <div className="text-xs text-green-600 mt-1">
                                  {meal.foods.slice(0, 3).map((f: any) => f.name).join(', ')}
                                  {meal.foods.length > 3 && ` +${meal.foods.length - 3} more`}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400 italic">No meal logged</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
